import { ProfileCreateRequest, ProfileCreateUpdateResponse, ProfileLevelType, ProfileResponse, ProfileUpdateRequest } from "@api/apiClient";
import { downloadBlobAsFile, useExportProfile } from "@api/profileExport";
import { useCreateProfile, useDeleteProfile, useProfileDetails, useUpdateProfile } from "@api/profiles";
import { GridToolbarImportButton } from "@components/DataGrid/components/toolbar/GridToolbarImportButton";
import MultiGridToolbarSearchFilter from "@components/DataGrid/components/toolbar/MultiGridToolbarSearchFilter";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, CircularProgress, Snackbar, Switch, Tooltip } from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridSlotsComponentsProps,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";
import { getDetailPanelContent, ProfileResponseModel } from "./ProfileDetailPanel";
import ProfileImportDisplay from "./ProfileImportDisplay";

// Custom TabPanel component
function CustomTabPanel(props: { children?: React.ReactNode; value: number; index: number }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-detail-tabpanel-${index}`} aria-labelledby={`profile-detail-tab-${index}`}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// Common grid columns for all tabs

const ProfileGrid = observer(() => {
  const [addDisabled, setAddDisabled] = useState(false);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [rows, setRows] = useState<ProfileResponseModel[]>([]);
  const [isImportMode, setIsImportMode] = useState(false);
  const [searchText, setSearchText] = useState("");
  const getProfiles = useProfileDetails();
  const apiRef = useGridApiRef();

  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const [coordinates, setCoordinates] = useState({ rowIndex: 0, colIndex: 0 });
  const [openSnackToast, setOpenSnackToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [exportingProfileId, setExportingProfileId] = useState<string | null>(null);

  const onError = (data: ProfileCreateUpdateResponse) => {
    if (data?.isDuplicate) {
      setOpenSnackToast(true);
      setToastMessage(i18n.t("profile-tab.notification.duplicate-profile-name"));
    }
  };

  const createProfiles = useCreateProfile({ onError });
  const updateProfiles = useUpdateProfile({ onError });
  const deleteProfiles = useDeleteProfile();
  const exportProfile = useExportProfile({
    onSuccess: (blob, variables) => {
      const profile = rows.find((r) => r.profileId === variables.profileId);
      const filename = `${profile?.name || "profile"}_export.xlsx`;
      downloadBlobAsFile(blob, filename);
      setOpenSnackToast(true);
      setToastMessage(i18n.t("profile-tab.notification.export-success"));
      setExportingProfileId(null);
    },
    onError: () => {
      setOpenSnackToast(true);
      setToastMessage(i18n.t("profile-tab.notification.export-error"));
      setExportingProfileId(null);
    },
  });

  useEffect(() => {
    apiRef.current.scrollToIndexes(coordinates);
    apiRef.current.setCellFocus(coordinates.rowIndex, "name");
  }, [apiRef, coordinates]);

  useEffect(() => {
    if (getProfiles.data) {
      const model: ProfileResponseModel[] = [];
      getProfiles.data.forEach((profile: ProfileResponse, index: number) => {
        console.log("Raw profile", profile);
        model.push({
          srNo: index + 1,
          profileId: profile.profileId,
          name: profile.name,
          description: profile.description,
          deleteStatus: profile.deleteStatus,
          isDeleted: profile.isDeleted,
          rebarEnabled: profile.rebarEnabled,
          ProfileConfigurationResponse: profile.profileConfigurationResponse,
        } as any);
      });

      setRows(model);
    }
  }, [getProfiles.data]);

  // Apply search filter to grid
  useEffect(() => {
    const quickFilterValues = searchText.split(" ").filter((word) => word !== "");
    apiRef.current.setQuickFilterValues(quickFilterValues);
  }, [searchText]);

  const columns: GridColDef[] = [
    {
      field: "srNo",
      headerName: i18n.t("profile-tab.grid.columns.id"),
      editable: false,
      flex: 1,
    },
    {
      field: "name",
      headerName: i18n.t("profile-tab.grid.columns.name"),
      editable: true,
      flex: 1,
    },
    {
      field: "description",
      headerName: i18n.t("profile-tab.grid.columns.description"),
      editable: true,
      flex: 1,
    },
    {
      field: "rebarEnabled",
      headerName: i18n.t("profile-tab.grid.columns.rebar-enabled"),
      type: "boolean",
      editable: true,
      flex: 1,
      renderCell: (params) => {
        console.log("renderCell value for rebarEnabled:", params.value);
        return params.value ? "✅ Enabled" : "❌ Disabled";
      },
      renderEditCell: (params) => {
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          event.preventDefault();
          console.log("Rebar enabled switch changed:", event.target.checked);
          const value = event.target.checked;
          // Just update the grid's edit model, don't call the API yet
          params.api.setEditCellValue({ id: params.id, field: params.field, value }, event);
        };

        return <Switch checked={Boolean(params.value)} onChange={handleChange} color="primary" inputProps={{ "aria-label": "Rebar enabled switch" }} />;
      },
    },

    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("profile-tab.grid.columns.actions"),
      resizable: false,
      width: 130,
      getActions: ({ id, row }) => {
        // do not allow to edit for default row
        if (row.name == "Default") return [];

        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              key={`save-${id}`}
              icon={
                <Tooltip title={i18n.t("profile-tab.grid.save-button.tooltip")}>
                  <SaveIcon />
                </Tooltip>
              }
              label={i18n.t("profile-tab.grid.save-button.label")}
              onClick={() => handleSaveClick(id)}
              sx={{
                color: "primary.main",
              }}
            />,
            <GridActionsCellItem
              key={`cancel-${id}`}
              icon={
                <Tooltip title={i18n.t("profile-tab.grid.cancel-button.tooltip")}>
                  <CancelIcon />
                </Tooltip>
              }
              label={i18n.t("profile-tab.grid.cancel-button.label")}
              onClick={() => handleCancelClick(id)}
            />,
          ];
        } else {
          return [
            <GridActionsCellItem
              key={`edit-${id}`}
              icon={
                <Tooltip title={i18n.t("profile-tab.grid.edit-button.tooltip")}>
                  <EditIcon />
                </Tooltip>
              }
              label={i18n.t("profile-tab.grid.edit-button.label")}
              onClick={() => handleEditClick(id)}
            />,
            <GridActionsCellItem
              key={`export-${id}`}
              icon={
                exportingProfileId === row.profileId ? (
                  <Tooltip title={i18n.t("profile-tab.grid.export-button.processing")}>
                    <CircularProgress size={20} />
                  </Tooltip>
                ) : (
                  <Tooltip title={i18n.t("profile-tab.grid.export-button.tooltip")}>
                    <DownloadIcon />
                  </Tooltip>
                )
              }
              label={i18n.t("profile-tab.grid.export-button.label")}
              onClick={() => handleExportClick(row)}
              disabled={exportingProfileId === row.profileId}
            />,
            <GridActionsCellItem
              key={`delete-${id}`}
              icon={
                <Tooltip title={i18n.t("profile-tab.grid.delete-button.tooltip")}>
                  <DeleteIcon />
                </Tooltip>
              }
              label={i18n.t("profile-tab.grid.delete-button.label")}
              onClick={() => handleDeleteClick(id, row)}
            />,
          ];
        }
      },
    },
  ];

  const handleAddRow = () => {
    const profileItem = {
      srNo: -1,
      name: "name",
      description: "description",
      rebarEnabled: false,
    } as ProfileResponseModel;

    setRows((oldRows: any) => [{ ...profileItem, isNew: true }, ...oldRows]);
    setAddDisabled(true);
    setTimeout(() => {
      apiRef.current.startRowEditMode({ id: -1 });
      setTimeout(() => {
        setCoordinates({ rowIndex: 0, colIndex: 0 });
      }, 1000);
    });
  };

  const handleEditClick = async (id: GridRowId) => {
    console.log("Edit clicked: ", id);
    apiRef.current.startRowEditMode({ id });
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleDeleteClick = async (id: GridRowId, row: any) => {
    console.log(id);
    if (!row?.profileId) {
      throw new Error("Unable to delete.");
    }

    await deleteProfiles.mutateAsync(row.profileId);
    getProfiles.refetch();
  };

  const handleExportClick = async (row: any) => {
    if (!row?.profileId) {
      setOpenSnackToast(true);
      setToastMessage(i18n.t("profile-tab.notification.export-error"));
      return;
    }

    try {
      setExportingProfileId(row.profileId);
      await exportProfile.mutateAsync({ profileId: row.profileId });
    } catch (error) {
      console.error("Export failed:", error);
      setExportingProfileId(null);
    }
  };

  const handleSaveClick = async (id: GridRowId) => {
    setAddDisabled(false);
    apiRef.current.stopRowEditMode({
      id,
      ignoreModifications: false,
    });
    setRowModesModel((prevModes) => {
      const updatedModes = { ...prevModes, [id]: { mode: GridRowModes.View } };
      return updatedModes;
    });
  };

  const handleCancelClick = (id: GridRowId) => {
    console.log("Cancel clicked: ", id);
    setAddDisabled(false);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View, ignoreModifications: true } });

    const newRows = rows.filter((r: any) => r.isNew !== true);
    setRows(newRows);
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    console.log("handleRowModesModelChange: ", newRowModesModel);
    setRowModesModel(newRowModesModel);
  };

  const handleCellEditStart: GridEventListener<"cellEditStart"> = (params, _event) => {
    console.log("handleCellEditStart: ", params);
    setAddDisabled(true);
  };

  const handleCellEditStop: GridEventListener<"cellEditStop"> = (params, _event) => {
    console.log("handleCellEditStop: ", params);
    setAddDisabled(false);
  };

  const handleRowEditStart: GridEventListener<"rowEditStart"> = (params, _event) => {
    console.log("handleRowEditStart: ", params);
    setAddDisabled(true);
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
    console.log("handleRowEditStop: ", params);

    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }

    setAddDisabled(rows.some((r: any) => r.isNew === true));
  };

  const processRowUpdate = async (newRow: GridRowModel, oldRow: GridRowModel) => {
    console.log(oldRow);
    console.log("🚀 processRowUpdate triggered");
    console.log("🔝 newRow:", newRow);
    if (newRow.ignoreModifications) {
      return newRow;
    }

    let response: ProfileCreateUpdateResponse;
    if (newRow.srNo === -1) {
      response = await createProfiles.mutateAsync(
        new ProfileCreateRequest({
          name: newRow.name,
          description: newRow.description,
          rebarEnabled: newRow.rebarEnabled,
        }) as any,
      );
    } else {
      response = await updateProfiles.mutateAsync(
        new ProfileUpdateRequest({
          profileId: newRow.profileId,
          name: newRow.name,
          description: newRow.description,
          rebarEnabled: newRow.rebarEnabled,
        }) as any,
      );
      console.log("🚀 response:", response);
    }

    // Handle nocontent response as successful save
    if (response === null || response === undefined || (response as any) === "nocontent") {
      getProfiles.refetch();
      return newRow;
    }

    if (response.isDuplicate) {
      setOpenSnackToast(true);
      setToastMessage(i18n.t("profile-tab.notification.duplicate-profile-name"));
    } else {
      getProfiles.refetch();
      return newRow;
    }
  };

  const [importFiles, setImportFiles] = useState<File[] | undefined>(undefined);

  const handleFileImport = async (files: File[]) => {
    // Store the original files array immediately to prevent garbage collection
    const filesCopy = [...files];
    setImportFiles(filesCopy);
    setIsImportMode(true);
  };

  const handleImportComplete = () => {
    setIsImportMode(false);
    getProfiles.refetch();
  };

  const handleImportCancel = () => {
    setIsImportMode(false);
    setImportFiles(undefined);
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
      {isImportMode ? (
        <ProfileImportDisplay files={importFiles} onImportComplete={handleImportComplete} onCancel={handleImportCancel} profileLevelType={ProfileLevelType.ApplicationImport} />
      ) : (
        <>
          <DataGrid
            apiRef={apiRef}
            loading={getProfiles.isFetching}
            columns={columns}
            rows={rows}
            getRowId={(row) => row?.srNo}
            hideFooter={false}
            editMode="row"
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onCellEditStart={handleCellEditStart}
            onCellEditStop={handleCellEditStop}
            onRowEditStart={handleRowEditStart}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={(error) => console.error("onProcessRowUpdateError: ", error)}
            rowExpander
            getDetailPanelContent={getDetailPanelContent}
            slots={{ toolbar: EditToolbar }}
            slotProps={{
              toolbar: {
                onAddRow: handleAddRow,
                onFileImport: handleFileImport,
                disabled: addDisabled,
                searchText,
                onSearchTextChange: (text: string | undefined) => setSearchText(text || ""),
              },
            }}
            sx={{ height: `${(height || 400) - 16}px` }}
          />

          {openSnackToast && (
            <Snackbar
              open={openSnackToast}
              autoHideDuration={2000}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              message={toastMessage}
              onClose={() => setOpenSnackToast(false)}
            />
          )}
        </>
      )}
    </Box>
  );
});

const EditToolbar = (
  props: NonNullable<GridSlotsComponentsProps["toolbar"]> & { onFileImport?: (files: File[]) => Promise<void>; searchText?: string; onSearchTextChange?: (text: string) => void },
) => {
  return (
    <GridToolbarContainer>
      <Tooltip title={i18n.t("profile-tab.grid.add-button.tooltip")}>
        <Button variant="outlined" onClick={() => props.onAddRow?.()} disabled={props.disabled}>
          {i18n.t("profile-tab.grid.add-button.label")}
        </Button>
      </Tooltip>
      <MultiGridToolbarSearchFilter value={props.searchText} onChange={props.onSearchTextChange} />
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarImportButton onFileImport={props.onFileImport} />
    </GridToolbarContainer>
  );
};

export default ProfileGrid;
